import { ShoppingBag, Settings, LogIn, MessageSquare, BarChart3, Bot } from "lucide-react";
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

export default function FloatingNav({ onOpenMarketplace, onOpenProviders, onOpenChats, onOpenEarnings, providerCount }) {
  const { user: clerkUser } = useUser();
  const navigate = useNavigate();
  const currentPath = window.location.pathname;

  const isActive = (path) => currentPath === path;
  const navButtonClass = (active) =>
    `flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all ${
      active
        ? 'text-[#080B10] bg-[#E5FF00]'
        : 'text-[#94A3B8] hover:text-white hover:bg-[#1C2433]'
    }`;

  return (
    <div
      data-testid="floating-nav"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 bg-[#111620]/95 backdrop-blur-xl border border-[#273142] rounded-full px-3 py-2 shadow-md"
    >
      <TooltipProvider delayDuration={200}>
        {/* Agents (shown on /chat) or Chats (shown on /agents) */}
        {isActive('/chat') ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid="nav-agents"
                onClick={() => navigate('/agents')}
                className={navButtonClass(false)}
              >
                <Bot size={16} />
                <span className="hidden sm:inline">Agents</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={8}><p className="text-xs">Agent internet</p></TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid="nav-chats"
                onClick={() => navigate('/chat')}
                className={navButtonClass(false)}
              >
                <MessageSquare size={16} />
                <span className="hidden sm:inline">Chats</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={8}><p className="text-xs">Your chats</p></TooltipContent>
          </Tooltip>
        )}

        <div className="w-px h-5 bg-[#273142]" />

        {/* Marketplace */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid="nav-marketplace"
              onClick={onOpenMarketplace}
              className={navButtonClass(isActive('/marketplace'))}
            >
              <ShoppingBag size={16} />
              <span className="hidden sm:inline">Marketplace</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={8}><p className="text-xs">Browse capsules</p></TooltipContent>
        </Tooltip>

        <div className="w-px h-5 bg-[#273142]" />

        {/* Earnings */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid="nav-earnings"
              onClick={onOpenEarnings}
              className={navButtonClass(isActive('/earnings'))}
            >
              <BarChart3 size={16} />
              <span className="hidden sm:inline">Earnings</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={8}><p className="text-xs">Creator earnings</p></TooltipContent>
        </Tooltip>

        <div className="w-px h-5 bg-[#273142]" />

        {/* Providers */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid="nav-providers"
              onClick={onOpenProviders}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-[#94A3B8] hover:text-white hover:bg-[#1C2433] transition-all text-xs font-medium relative"
            >
              <Settings size={16} />
              <span className="hidden sm:inline">Providers</span>
              {providerCount > 0 && (
                <span className="ml-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E5FF00] text-[#080B10] text-[10px] font-mono font-bold">
                  {providerCount}
                </span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={8}><p className="text-xs">Manage LLM providers</p></TooltipContent>
        </Tooltip>

        <div className="w-px h-5 bg-[#273142]" />

        {/* Auth */}
        <div className="px-2 py-1">
          <SignedOut>
            <SignInButton mode="modal">
              <button
                data-testid="nav-sign-in"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#E5FF00] text-[#080B10] hover:bg-[#D4EC00] transition-all text-xs font-semibold"
              >
                <LogIn size={14} />
                <span>Sign In</span>
              </button>
            </SignInButton>
          </SignedOut>
          <SignedIn>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#94A3B8] hidden sm:inline">{clerkUser?.firstName}</span>
              <UserButton appearance={{ elements: { userButtonAvatarBox: "w-7 h-7" } }} />
            </div>
          </SignedIn>
        </div>
      </TooltipProvider>
    </div>
  );
}
